import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation } from '@apollo/client';
import dayjs from 'dayjs';

import Card from '../../atoms/Card';
import { TextAreaField } from '../../atoms/TextArea';

import './MockBarnSetupCard.scss';
import { Formik } from 'formik';
import { TextInputField } from '../../atoms/TextInput';
import Button from '../../atoms/Button';
import Select, { SelectField } from '../../atoms/Select';
import { MOCK_BARN_PRESETS, MOCK_BARN_PERSET_MAP } from './enum';
import { toast } from 'react-toastify';

const DEFAULT_MOCK_ORG_ID = '2f2a0ed0-528f-4b0e-a1fa-bcb9b47430ec';
const MOCK_SETTINGS_VERSION = '5';
const BARN_SETUP_ONE = gql`
  mutation InsertMockBarnStepOne($barn: farm_insert_input!) {
    insert_farm_one(object: $barn) {
      id
      bins {
        id
        name
      }
      bins {
        id
        name
      }
      bin_sets {
        id
      }
      feed_lines {
        id
        name
        device_assignments {
          device {
            id
            serial
          }
        }
        feeders {
          capacity_in_grams
        }
      }
      rooms {
        id
        name
      }
    }
  }
`;

const BARN_SETUP_TWO = gql`
  mutation InsertMockBarnStepTwo(
    $bin_feed_lines: [bin_feed_line_insert_input!]!
    $binUpdates: [bin_updates!]!
    $feedLineUpdates: [feed_line_updates!]!
    $mockBarnSettings: mock_barn_simulation_settings_insert_input!
    $room_feed_line: [room_feed_line_insert_input!]!
  ) {
    update_bin_many(updates: $binUpdates) {
      returning {
        id
      }
    }
    update_feed_line_many(updates: $feedLineUpdates) {
      returning {
        id
      }
    }
    insert_mock_barn_simulation_settings_one(object: $mockBarnSettings) {
      settings
    }
    insert_bin_feed_line(objects: $bin_feed_lines) {
      affected_rows
      returning {
        bin_id
        feed_line_id
      }
    }
    insert_room_feed_line(objects: $room_feed_line) {
      returning {
        id
      }
    }
  }
`;

const generate_barn_setup_two_variables = (now, stepOneFarmData, settingsTemplate, orgId, secondsStartedAgo) => {
  const start_time = now.unix() - secondsStartedAgo;
  const barn_id = stepOneFarmData?.id;

  const all_bins = stepOneFarmData.bins;
  const all_rooms = stepOneFarmData.rooms;
  const all_feed_lines = stepOneFarmData.feed_lines;
  const bin_sets = stepOneFarmData.bin_sets.map((bin_set, i_bin_set) => {
    // Calculate how many bins to assign to this bin set
    const bin_quotient = Math.floor(all_bins.length / stepOneFarmData.bin_sets.length);
    const bin_remainder = all_bins.length % stepOneFarmData.bin_sets.length;

    // Determine the start and end indices for the bins assigned to this bin set
    const start_index = i_bin_set * bin_quotient + Math.min(i_bin_set, bin_remainder);
    const end_index = start_index + bin_quotient + (i_bin_set < bin_remainder ? 1 : 0);

    // Get the bins for this bin set
    const bins = all_bins.slice(start_index, end_index);

    // Calculate how many feed lines to assign to this bin set
    const feed_line_quotient = Math.floor(all_feed_lines.length / stepOneFarmData.bin_sets.length);
    const feed_line_remainder = all_feed_lines.length % stepOneFarmData.bin_sets.length;

    // Determine the start and end indices for the feed lines assigned to this bin set
    const feed_line_start_index = i_bin_set * feed_line_quotient + Math.min(i_bin_set, feed_line_remainder);
    const feed_line_end_index = feed_line_start_index + feed_line_quotient + (i_bin_set < feed_line_remainder ? 1 : 0);

    // Get the feed lines for this bin set
    const feed_lines = all_feed_lines.slice(feed_line_start_index, feed_line_end_index);

    return {
      id: bin_set.id,
      bins: bins.map((b) => ({
        id: b.id,
      })),
      feed_lines: feed_lines.map((f) => ({
        id: f.id,
        sensor: {
          id: f?.device_assignments[0]?.device?.id,
          serial: f?.device_assignments[0]?.device?.serial,
        },
      })),
    };
  });

  const bin_feed_lines = bin_sets.reduce((arr, b_s) => {
    b_s.bins.forEach((b) => {
      b_s.feed_lines.forEach((fl) => {
        arr.push({
          bin_id: b.id,
          feed_line_id: fl.id,
        });
      });
    });
    return arr;
  }, []);

  const finalSettings = {
    ...settingsTemplate,
    org_id: orgId,
    barn_structure: {
      barn_id: barn_id,
      bin_sets: bin_sets,
      rooms: all_rooms.map((r) => ({
        id: r.id,
        name: r.name,
      })),
    },
    sim_start_time: start_time,
  };

  const binUpdates = [];
  bin_sets.forEach((b_s) => {
    b_s.bins.forEach((b) => {
      binUpdates.push({
        _set: {
          bin_set_id: b_s.id,
        },
        where: {
          id: { _eq: b.id },
        },
      });
    });
  });

  const feedLineUpdates = [];
  bin_sets.forEach((b_s) => {
    b_s.feed_lines.forEach((fl) => {
      feedLineUpdates.push({
        _set: {
          bin_set_id: b_s.id,
        },
        where: {
          id: { _eq: fl.id },
        },
      });
    });
  });

  return {
    mockBarnSettings: {
      barn_id: barn_id,
      version: MOCK_SETTINGS_VERSION,
      settings: finalSettings,
    },
    binUpdates,
    bin_feed_lines,
    feedLineUpdates,
    room_feed_line: all_feed_lines.map((f) => {
      return {
        feed_line_id: f.id,
        room_id: all_rooms.find((r) => {
          return f.name.toLowerCase().includes(r.name.toLowerCase());
        })?.id,
      };
    }),
  };
};

function generateRandomString(length) {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

const generate_barn_setup_one_variables = (now, name, organization_id, secondsStartedAgo) => {
  const start_time = now.subtract(secondsStartedAgo, 'seconds').unix();

  return {
    barn: {
      organization_id,
      name: name,
      address: '123 Fake St',
      bins: {
        data: [
          {
            name: '1',
            capacity_in_grams: 14514960,
          },
          {
            name: '2',
            capacity_in_grams: 14514960,
          },
          {
            name: '3',
            capacity_in_grams: 14514960,
          },
          {
            name: '4',
            capacity_in_grams: 14514960,
          },
        ],
      },
      bin_sets: {
        data: [
          {
            bin_set_levels: {
              data: [
                {
                  level_in_grams: 0,
                  valid_at: start_time,
                  purpose: 'reset',
                },
              ],
            },
          },
          {
            bin_set_levels: {
              data: [
                {
                  level_in_grams: 0,
                  valid_at: start_time,
                  purpose: 'reset',
                },
              ],
            },
          },
        ],
      },
      rooms: {
        data: [
          {
            name: 'North',
          },
          { name: 'South' },
        ],
      },
      feed_lines: {
        data: [
          {
            name: 'North East',
            device_assignments: {
              data: [
                {
                  started_at: start_time,
                  status: 'active',
                  device: {
                    data: {
                      serial: `MOCK-${generateRandomString(6)}`,
                      type: 'FeedLine',
                      hw_version: 'mock',
                    },
                  },
                },
              ],
            },
            feeders: { data: [{ capacity_in_grams: 136077, quantity: 10 }] },
          },
          {
            name: 'North West',
            device_assignments: {
              data: [
                {
                  started_at: start_time,
                  status: 'active',
                  device: {
                    data: {
                      serial: `MOCK-${generateRandomString(6)}`,
                      type: 'FeedLine',
                      hw_version: 'mock',
                    },
                  },
                },
              ],
            },
            feeders: { data: [{ capacity_in_grams: 136077, quantity: 10 }] },
          },
          {
            name: 'South East',
            device_assignments: {
              data: [
                {
                  started_at: start_time,
                  status: 'active',
                  device: {
                    data: {
                      serial: `MOCK-${generateRandomString(6)}`,
                      type: 'FeedLine',
                      hw_version: 'mock',
                    },
                  },
                },
              ],
            },
            feeders: { data: [{ capacity_in_grams: 136077, quantity: 10 }] },
          },
          {
            name: 'South West',
            device_assignments: {
              data: [
                {
                  started_at: start_time,
                  status: 'active',
                  device: {
                    data: {
                      serial: `MOCK-${generateRandomString(6)}`,
                      type: 'FeedLine',
                      hw_version: 'mock',
                    },
                  },
                },
              ],
            },
            feeders: { data: [{ capacity_in_grams: 136077, quantity: 10 }] },
          },
        ],
      },
    },
  };
};

function MockBarnSetupCard({ onSuccess, onError }) {
  const [settings, setSettings] = useState();
  const [curOrgId, setCurOrgId] = useState();
  const [secondsStartedAgo, setSecondsStartedAgo] = useState(6 * 30 * 24 * 60 * 60); // Roughly N Months Ago
  const nowJS = useMemo(() => {
    return dayjs();
  }, []);

  const [insert_barn_step_one, { loading: stepOneLoading }] = useMutation(BARN_SETUP_ONE, {
    onCompleted: (insert_barn_step_one_results) => {
      const variables = generate_barn_setup_two_variables(
        nowJS,
        insert_barn_step_one_results.insert_farm_one,
        settings,
        curOrgId,
        secondsStartedAgo,
      );
      insert_barn_step_two({ variables, refetchQueries: ['MockBarnDataQuery'] });
    },
    onError: () => {
      toast.warn('Error Occurred Step 1/2', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      onError?.();
    },
  });
  const [insert_barn_step_two, { loading: stepTwoLoading }] = useMutation(BARN_SETUP_TWO, {
    onCompleted: (insert_barn_step_two_results) => {
      console.log(insert_barn_step_two_results);
      toast.success('Successfully Updated', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      onSuccess?.();
    },
    onError: () => {
      toast.warn('Error Occurred Step 2/2', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      onError?.();
    },
  });

  return (
    <Card className="mockBarnSetupCard">
      <Formik
        onSubmit={(values) => {
          setSettings(JSON.parse(values.settings_json));
          setCurOrgId(values.org_id);
          setSecondsStartedAgo(values.secondsStartedAgo);
          const variables = generate_barn_setup_one_variables(
            nowJS,
            values.name,
            values.org_id,
            values.secondsStartedAgo,
          );
          insert_barn_step_one({ variables });
        }}
        initialValues={{
          name: `Mock Barn #${generateRandomString(6)}`,
          org_id: DEFAULT_MOCK_ORG_ID,
          secondsStartedAgo: 6 * 30 * 24 * 60 * 60,
        }}
      >
        {({ handleSubmit, setFieldValue, values, errors }) => {
          return (
            <div className="mockSetupForm">
              <TextInputField label={'Name'} name={'name'}></TextInputField>
              <TextInputField label={'Organization ID:'} name={'org_id'}></TextInputField>
              <SelectField
                label="secondsStartedAgo"
                name="secondsStartedAgo"
                itemList={[
                  { value: 7 * 24 * 60 * 60, name: '1 Week Ago' },

                  { value: 3 * 30 * 24 * 60 * 60, name: '3 Months Ago' },
                  { value: 4 * 30 * 24 * 60 * 60, name: '4 Months Ago' },
                  { value: 5 * 30 * 24 * 60 * 60, name: '5 Months Ago' },
                  { value: 6 * 30 * 24 * 60 * 60, name: '6 Months Ago' },
                  { value: 7 * 30 * 24 * 60 * 60, name: '7 Months Ago' },
                  { value: 8 * 30 * 24 * 60 * 60, name: '8 Months Ago' },
                ]}
              />
              <Select
                label="Choose a preset"
                itemList={MOCK_BARN_PRESETS.map((c) => {
                  return { value: c.value, name: c.name };
                })}
                value={values.preset}
                description={errors?.preset}
                onChange={(e) => {
                  setFieldValue('preset', e.target.value);
                  setFieldValue('settings_json', JSON.stringify(MOCK_BARN_PERSET_MAP[e.target.value].json, null, 4));
                }}
              />
              <div>Settings Version: {MOCK_SETTINGS_VERSION}</div>
              <TextAreaField label={'Settings'} name={'settings_json'}></TextAreaField>
              <Button
                loading={stepOneLoading || stepTwoLoading}
                disabled={stepOneLoading || stepTwoLoading}
                variant="vivid"
                color="success"
                content={'Save'}
                onClick={handleSubmit}
              />
            </div>
          );
        }}
      </Formik>
    </Card>
  );
}

MockBarnSetupCard.propTypes = {
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};

export default MockBarnSetupCard;
